import { DateFilterType, SortingListingType } from '@/api/algolia/types';
import Modal from '@/app/_components/modal';
import LocationAutocomplete from '@/app/_components/searchbar/_components/LocationAutocomplete';
import { FIND_BEST_CLINIC_SEARCH_PARAM, FIND_BEST_CLINIC_SEARCH_PARAM_VALUE } from '@/app/_components/widgets/listing/_constants';
import { ROUTES } from '@/routes';
import { ELButton, ELLoader, ELTagButton, ELTextField } from '@davincihealthcare/elty-design-system-react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ArrowLongLeftIcon, BanknotesIcon, CalendarIcon, MapPinIcon } from '@heroicons/react/24/solid';
import { useRouter } from 'next/navigation';
import { ChangeEvent, ReactNode, useState } from 'react';
import { posthogTracker } from '@/utils/trackingPostHog';

type ToggleTagButtonGroupProps<TValue extends string> = {
  label?: string;
  icon: ReactNode;
  items: {
    label: string;
    value: TValue;
  }[];
  selectedValue?: TValue;
  onSelect: (value: TValue) => void;
};

const ToggleTagButtonGroup = <TValue extends string>({
  label,
  icon,
  items,
  selectedValue,
  onSelect,
}: ToggleTagButtonGroupProps<TValue>) => {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row items-center gap-2">
        {icon}
        <span className="text-base font-semibold text-primary">{label}</span>
      </div>

      <div className="flex flex-row flex-wrap gap-3 [&>button]:border-none">
        {items.map(item => {
          if (selectedValue !== undefined && item.value !== selectedValue) return null;

          return (
            <ELTagButton
              key={item.value}
              state={selectedValue === item.value ? 'active' : 'focused'}
              label={item.label}
              onClick={() => onSelect(item.value)}
            />
          );
        })}
      </div>
    </div>
  );
};

enum DateOptionType {
  ASAP = 'asap',
  THREE_DAYS = '3days',
  WEEK = 'week',
  FLEXIBLE = 'flexible',
}

enum PriceOptionType {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

type ModalFindBestClinicProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  service?: string;
};

type FindBestClinicArgs = {
  dateOption?: DateOptionType;
  priceOption?: PriceOptionType;
  address?: string;
  locationCoordinates?: { latitude: number; longitude: number };
};

const hasValidCoordinates = (
  coordinates?: FindBestClinicArgs['locationCoordinates'],
): coordinates is NonNullable<FindBestClinicArgs['locationCoordinates']> => {
  return coordinates !== undefined && Number.isFinite(coordinates.latitude) && Number.isFinite(coordinates.longitude);
};

const ModalFindBestClinic: React.FC<ModalFindBestClinicProps> = ({ isOpen, setIsOpen, service }) => {
  const router = useRouter();

  const [args, setArgs] = useState<FindBestClinicArgs>({});
  const [isAddressValid, setIsAddressValid] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setAddress = (value: string | undefined) => {
    setArgs(prev => ({ ...prev, address: value }));
    setIsAddressValid(value !== undefined && value.trim() !== '');
  };

  const handleSubmit = () => {
    if (!args.address || !hasValidCoordinates(args.locationCoordinates)) {
      setIsAddressValid(false);
      return;
    }

    setIsSubmitting(true);

    const params = new URLSearchParams({
      [FIND_BEST_CLINIC_SEARCH_PARAM]: FIND_BEST_CLINIC_SEARCH_PARAM_VALUE,
      location: args.address,
      lat: args.locationCoordinates.latitude.toString(),
      lng: args.locationCoordinates.longitude.toString(),
    });

    if (service) {
      params.set('service', service);
    }

    if (args.dateOption === DateOptionType.THREE_DAYS) {
      params.set('dateFilter', DateFilterType.three_days);
      params.set('order', SortingListingType.firstAvailabilityAsc);
    } else if (args.dateOption === DateOptionType.WEEK) {
      params.set('dateFilter', DateFilterType.seven_days);
      params.set('order', SortingListingType.firstAvailabilityAsc);
    } else if (args.dateOption === DateOptionType.ASAP) {
      params.set('order', SortingListingType.firstAvailabilityAsc);
    }

    if (args.priceOption === PriceOptionType.MEDIUM && args.dateOption !== DateOptionType.ASAP) {
      params.set('order', SortingListingType.priceAsc);
    } else if (args.priceOption === PriceOptionType.HIGH) {
      params.set('order', SortingListingType.priceAsc);
    }

    posthogTracker.captureFindBestClinicSubmit({
      dateOption: args.dateOption,
      priceOption: args.priceOption,
      location: args.address,
    });

    router.push(`${ROUTES.BOOKING.pathname}?${params.toString()}`);
  };

  const close = () => {
    setArgs({});
    setIsAddressValid(true);
    setIsOpen(false);
    setIsSubmitting(false);
  };

  return (
    <Modal
      isModalOpen={isOpen}
      onClose={close}
      className={
        isOpen
          ? 'm-0 flex h-full max-h-full w-full max-w-full animate-slideLeft flex-col items-stretch justify-between gap-8 px-4 py-6 backdrop:bg-black/50 open:animate-slideRight md:ml-auto md:max-w-2xl md:items-start md:justify-start md:px-20 md:py-16'
          : ''
      }
    >
      <div className="flex flex-col gap-8">
        <div className="flex flex-col items-start gap-4 md:gap-6">
          <ELButton
            label="Torna indietro"
            variant="text"
            color="primary"
            size="medium"
            leadingIcon={<ArrowLongLeftIcon />}
            onClick={close}
          />
          <h6 className="text-2xl font-bold text-primary">Trova la clinica migliore per te</h6>
          <p className="text-sm font-medium text-primary">
            Rispondi a 3 semplici domande e trova la struttura più indicata per le tue esigenze
          </p>
        </div>

        <div className="flex flex-col gap-4 rounded-lg border border-2 border-brandBlue-300 p-4 md:gap-5 [&>div:not(:last-child)]:border-b-2 [&>div:not(:last-child)]:border-brandBlue-300 [&>div:not(:last-child)]:pb-4 [&>div:not(:last-child)]:md:pb-5">
          <ToggleTagButtonGroup
            label="Quando vuoi fare la visita?"
            icon={<CalendarIcon className="size-6 text-primary-active" />}
            items={[
              { label: 'Prima possibile', value: DateOptionType.ASAP },
              { label: 'Sono flessibile', value: DateOptionType.FLEXIBLE },
            ]}
            selectedValue={args.dateOption}
            onSelect={value => setArgs(prev => ({ ...prev, dateOption: value }))}
          />

          {args.dateOption && (
            <ToggleTagButtonGroup
              label="Quanto conta il prezzo?"
              icon={<BanknotesIcon className="size-6 text-primary-active" />}
              items={[
                { label: 'Poco', value: PriceOptionType.LOW },
                { label: 'Abbastanza', value: PriceOptionType.MEDIUM },
                { label: 'Molto', value: PriceOptionType.HIGH },
              ]}
              selectedValue={args.priceOption}
              onSelect={value => setArgs(prev => ({ ...prev, priceOption: value }))}
            />
          )}

          {args.priceOption && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-row items-center gap-2">
                <MapPinIcon className="size-6 text-primary-active" />
                <span className="text-base font-semibold text-primary">Vicino a quale indirizzo?</span>
              </div>

              <LocationAutocomplete
                position="standalone"
                id="location"
                size="sm"
                value={args.address}
                setValue={setAddress}
                onOptionClick={option => setAddress(option.value)}
                setLocationCoordinates={([lat, long]) =>
                  setArgs(prev => ({
                    ...prev,
                    locationCoordinates: Number.isFinite(lat) && Number.isFinite(long) ? { latitude: lat, longitude: long } : undefined,
                  }))
                }
              >
                {props => (
                  <ELTextField
                    placeholder="Es. Via Roma 1, Milano"
                    aria-label="label"
                    name="name"
                    leadingIcon={<MagnifyingGlassIcon />}
                    value={props.value}
                    onChange={(ev: ChangeEvent<HTMLInputElement>) => {
                      props.setValue(ev.currentTarget.value);
                    }}
                    error={isAddressValid ? undefined : 'Indirizzo non valido'}
                  />
                )}
              </LocationAutocomplete>
            </div>
          )}
        </div>
      </div>

      {args.dateOption && args.priceOption && (
        <div className="[&>button]:w-full [&>button]:px-16 [&>button]:md:w-auto">
          <ELButton
            label="Continua"
            color="primary"
            size="medium"
            onClick={handleSubmit}
            variant="floating"
            trailingIcon={isSubmitting && <ELLoader className="-mt-1 h-auto" />}
            disabled={isSubmitting}
          />
        </div>
      )}
    </Modal>
  );
};

export default ModalFindBestClinic;
